import React, { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Plus, Minus, Trash2, Wrench, Camera, ImagePlus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ordersApi, productsApi, uploadsApi } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency, formatApiError, Spinner } from '../utils/helpers';
import { useLanguage } from '../contexts/LanguageContext';

function DiagItemRow({ item, index, products, onUpdate, onRemove }) {
  return (
    <div className="flex gap-3 items-start p-3 bg-slate-50 rounded-xl">
      <div className="flex-1 min-w-0">
        <select value={item.product_id}
          onChange={e => {
            const p = products.find(p => p.id === e.target.value);
            onUpdate(index, { product_id: p?.id || '', product_name: p?.name || '', unit: p?.unit || 'unit', price: p?.price || 0 });
          }}
          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] mb-2">
          <option value="">Pilih layanan / suku cadang...</option>
          {products.map(p => <option key={p.id} value={p.id}>{p.name} — {formatCurrency(p.price)}</option>)}
        </select>
        {!item.product_id && (
          <input value={item.product_name} onChange={e => onUpdate(index, { product_name: e.target.value })}
            placeholder="Atau ketik nama layanan/part secara manual..."
            className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none mb-2" />
        )}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Qty ({item.unit || 'unit'})</label>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => onUpdate(index, { quantity: Math.max(0.1, +(item.quantity - 1).toFixed(1)) })} className="w-7 h-7 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><Minus size={12} /></button>
              <input type="number" step="0.1" min="0.1" value={item.quantity} onChange={e => onUpdate(index, { quantity: parseFloat(e.target.value) || 1 })} className="w-16 text-center py-1 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" />
              <button type="button" onClick={() => onUpdate(index, { quantity: +(item.quantity + 1).toFixed(1) })} className="w-7 h-7 rounded-lg bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><Plus size={12} /></button>
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Harga</label>
            <input type="number" value={item.price} onChange={e => onUpdate(index, { price: parseFloat(e.target.value) || 0 })} className="w-full py-1.5 px-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="text-right pt-1 flex-shrink-0">
        <p className="text-xs text-slate-400 mb-1">Subtotal</p>
        <p className="text-sm font-semibold text-slate-800">{formatCurrency(item.quantity * item.price)}</p>
        <button type="button" onClick={() => onRemove(index)} className="mt-2 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function DiagnosisModal({ order, onClose, onSuccess }) {
  const { t } = useLanguage();
  const fileInputRef = useRef();
  const [items, setItems] = useState(
    (order.items || []).length > 0 && order.items[0].price > 0
      ? order.items
      : [{ product_id: '', product_name: '', unit: 'unit', quantity: 1, price: 0 }]
  );
  const [diagNotes, setDiagNotes] = useState(order.diagnosis_notes || order.condition_notes || '');
  const [estCompletion, setEstCompletion] = useState(order.estimated_completion || '');
  const [photos, setPhotos] = useState(order.condition_photos || []); // [{path, url, name}]
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const { data: products = [] } = useQuery({ queryKey: ['products'], queryFn: () => productsApi.list().then(r => r.data) });

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 5) {
      toast.error(t('Maksimal 5 foto', 'Max 5 photos'));
      return;
    }
    setUploadingPhoto(true);
    for (const file of files) {
      try {
        const res = await uploadsApi.uploadPhoto(file);
        setPhotos(prev => [...prev, { path: res.data.path, url: uploadsApi.getPhotoUrl(res.data.path), name: file.name }]);
      } catch (err) {
        toast.error(`Gagal upload: ${file.name}`);
      }
    }
    setUploadingPhoto(false);
    e.target.value = '';
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const total = items.reduce((s, i) => s + (i.quantity * i.price), 0);

  const mutation = useMutation({
    mutationFn: () => ordersApi.updateDiagnosis(order.id, {
      items: items.map(i => ({
        product_id: i.product_id || '',
        product_name: i.product_name || 'Item Servis',
        unit: i.unit || 'unit',
        quantity: i.quantity,
        price: i.price,
      })),
      diagnosis_notes: diagNotes,
      estimated_completion: estCompletion || null,
      condition_photos: photos.map(p => p.path),
    }),
    onSuccess: () => {
      toast.success(t('Diagnosa berhasil disimpan!', 'Diagnosis saved!'));
      onSuccess?.();
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const updateItem = (i, c) => setItems(arr => arr.map((item, idx) => idx === i ? { ...item, ...c } : item));
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const addItem = () => setItems(arr => [...arr, { product_id: '', product_name: '', unit: 'unit', quantity: 1, price: 0 }]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4 animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
                <Wrench size={16} className="text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-800 font-manrope">{t('Input Diagnosa & Harga', 'Diagnosis & Pricing')}</h3>
            </div>
            <p className="text-xs text-slate-400 mt-0.5 ml-10">{order.tracking_code} · {order.customer_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100"><X size={18} className="text-slate-500" /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Kondisi awal */}
          {order.condition_notes && (
            <div className="bg-slate-50 rounded-xl p-4 border-l-4 border-slate-300">
              <p className="text-xs font-semibold text-slate-500 mb-1">{t('Kondisi & Keluhan Awal', 'Initial Condition & Complaint')}</p>
              <p className="text-sm text-slate-700">{order.condition_notes}</p>
              {order.device_brand && <p className="text-xs text-slate-500 mt-1">Merk/Model: <strong>{order.device_brand}</strong></p>}
            </div>
          )}

          {/* Items / Biaya Servis */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-slate-800 font-manrope text-sm">{t('Rincian Biaya Servis & Part', 'Service Cost & Parts')}</p>
              <button type="button" onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-[#0f6c44] rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors"><Plus size={14} />{t('Tambah Item', 'Add Item')}</button>
            </div>
            <div className="space-y-3">
              {items.map((item, i) => (
                <DiagItemRow key={i} item={item} index={i} products={products} onUpdate={updateItem} onRemove={removeItem} />
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="bg-emerald-50 rounded-xl p-4 flex justify-between items-center border border-emerald-100">
            <span className="font-semibold text-slate-700">{t('Total Biaya', 'Total Cost')}</span>
            <span className="text-xl font-extrabold text-[#0f6c44] font-manrope">{formatCurrency(total)}</span>
          </div>

          {/* Diagnosis notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Catatan Diagnosa / Hasil Temuan', 'Diagnosis Notes / Findings')}</label>
            <textarea value={diagNotes} onChange={e => setDiagNotes(e.target.value)} rows={3}
              placeholder={t('Contoh: Ganti IC power, baterai kembung, perlu ganti lcd...', 'e.g. Replace power IC, swollen battery, LCD needs replacement...')}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] resize-none" />
          </div>

          {/* Est Completion */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('Estimasi Selesai', 'Est. Completion')}</label>
            <input type="date" value={estCompletion} onChange={e => setEstCompletion(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>

          {/* Foto Kondisi Barang */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-700">{t('Foto Kondisi Barang', 'Condition Photos')}</label>
              <span className="text-xs text-slate-400">{photos.length}/5</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-200 group">
                  <img src={p.url} alt={p.name || 'foto'} className="w-full h-full object-cover" />
                  <button onClick={() => removePhoto(idx)}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 size={16} className="text-white" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}
                  className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-[#0f6c44] hover:bg-emerald-50 transition-all text-slate-400 hover:text-[#0f6c44]">
                  {uploadingPhoto
                    ? <Spinner size="sm" className="border-slate-200 border-t-[#0f6c44]" />
                    : <><ImagePlus size={18} /><span className="text-xs">Upload</span></>
                  }
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              onChange={handlePhotoSelect} data-testid="photo-upload-input" />
            <p className="text-xs text-slate-400 mt-1.5">{t('Maks. 5 foto, 8MB per foto (jpg/png/webp)', 'Max 5 photos, 8MB each (jpg/png/webp)')}</p>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-slate-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors">
            {t('Batal', 'Cancel')}
          </button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || total === 0}
            className="flex-1 h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70">
            {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan Diagnosa', 'Save Diagnosis')}
          </button>
        </div>
      </div>
    </div>
  );
}
